import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import AccountStatusBadge from "../components/AccountStatusBadge";
import CreateDeletionCaseModal from "../components/CreateDeletionCaseModal";
import ProfessionalBenefitsBanner from "../components/ProfessionalBenefitsBanner";
import ReviewHubCard from "../components/reviewHub/ReviewHubCard";
import ReviewHubViewTabs from "../components/reviewHub/ReviewHubViewTabs";
import ReviewSelectionPricingPanel from "../components/ReviewSelectionPricingPanel";
import ReviewsMetricsCards from "../components/ReviewsMetricsCards";
import { GoogleBusinessLogo, JamedaLogo } from "../platformCatalog";
import {
  hydrateConnectionsFromServer,
  loadReviewLocations,
} from "../platformConnectionsStorage";
import { syncJameda } from "../jamedaApi";
import {
  fetchLocationReviews,
  fetchLocationStats,
  postReviewReply,
  syncLocationReviews,
} from "../reviewsApi";
import { formatCaseId } from "../casesApi";
import { loadClientSettings } from "../clientPreferencesStorage";
import {
  loadResolvedReviewIds,
  syncResolvedReviewIds,
} from "../resolvedReviewsStorage";
import useClientAccountStatus from "../hooks/useClientAccountStatus";
import useSubscriptionBenefits from "../hooks/useSubscriptionBenefits";
import { billingPlanForAccountStatus } from "../../../lib/accountStatus";
import {
  getBillingPlan,
  setBillingPlan as persistBillingPlan,
} from "../billingPlanStorage";
import { useTranslation } from "../../../i18n";

const HUB_BATCH_SIZE = 200;
const ALL_PAGE_SIZE = 20;

function isCriticalRating(rating) {
  const value = Number(rating);
  return Number.isFinite(value) && value >= 1 && value <= 3;
}

function filterByView(reviews, view, resolvedIds, showResolved) {
  const visible = showResolved
    ? reviews
    : reviews.filter((review) => !resolvedIds.has(review.id));

  switch (view) {
    case "needs-reply":
      return visible.filter((review) => !review.reply?.comment);
    case "critical":
      return visible.filter((review) => isCriticalRating(review.rating));
    default:
      return visible;
  }
}

function countByView(reviews, resolvedIds, stats) {
  const visible = reviews.filter((review) => !resolvedIds.has(review.id));
  return {
    "needs-reply": visible.filter((review) => !review.reply?.comment).length,
    critical: stats?.negative ?? visible.filter((review) => isCriticalRating(review.rating)).length,
    all: stats?.totalReviews ?? visible.length,
  };
}

const ClientReviewHubPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectionAppliedRef = useRef(false);

  // Land on the Critical tab by default — that's where the urgent reviews are.
  const hubView = searchParams.get("view") || "critical";
  const setHubView = (view) => {
    setSearchParams({ view }, { replace: true });
  };

  const [locations, setLocations] = useState(() => loadReviewLocations());
  const [selectedLocationId, setSelectedLocationId] = useState(
    () => locations[0]?.locationId ?? null
  );

  // The backend owns the connection list: team members (and this user on a
  // new device) get the company's connected locations even with an empty
  // local cache.
  useEffect(() => {
    let active = true;
    hydrateConnectionsFromServer().then((list) => {
      if (!active || list === null) return;
      setLocations(loadReviewLocations());
    });
    return () => {
      active = false;
    };
  }, []);

  const [stats, setStats] = useState(null);
  const [hubReviews, setHubReviews] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  // True until the first data load resolves — prevents an "all caught up"
  // flash before the initial fetch has run.
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [syncMessage, setSyncMessage] = useState("");

  const [expandedReplyId, setExpandedReplyId] = useState(null);
  const [pendingReplies, setPendingReplies] = useState({});
  const [sendingReviewId, setSendingReviewId] = useState(null);
  const [resolvedIds, setResolvedIds] = useState(() => loadResolvedReviewIds());

  // Reconcile the resolved-review set with the server on mount (authoritative).
  useEffect(() => {
    syncResolvedReviewIds().then(setResolvedIds);
  }, []);
  const showResolved = loadClientSettings().showResolvedInInbox;

  const [selectedById, setSelectedById] = useState({});
  const [caseModalOpen, setCaseModalOpen] = useState(false);

  const { accountStatus, isPaid } = useClientAccountStatus();
  const {
    benefits,
    freeRemaining,
    freeAllowance,
    freeUsed,
    periodEnd,
    refresh: refreshBenefits,
  } = useSubscriptionBenefits(accountStatus, { enabled: isPaid });
  const [billingPlan, setBillingPlan] = useState(() => getBillingPlan());

  useEffect(() => {
    const plan = billingPlanForAccountStatus(accountStatus);
    persistBillingPlan(plan);
    setBillingPlan(plan);
  }, [accountStatus]);

  useEffect(() => {
    if (locations.length > 0 && !selectedLocationId) {
      setSelectedLocationId(locations[0].locationId);
    }
  }, [locations, selectedLocationId]);

  useEffect(() => {
    setPage(1);
    setSelectedById({});
    setExpandedReplyId(null);
  }, [selectedLocationId, hubView]);

  useEffect(() => {
    const nav = location.state;
    if (!nav) return;

    if (nav.openView) {
      setHubView(nav.openView);
    } else if (nav.preselectedReviews?.length) {
      setHubView("critical");
    }

    if (nav.preselectedReviews?.length && !preselectionAppliedRef.current) {
      if (nav.selectedLocationId) {
        setSelectedLocationId(nav.selectedLocationId);
      }
      setSelectedById((prev) => {
        const next = { ...prev };
        nav.preselectedReviews.forEach((review) => {
          if (review?.id) next[review.id] = review;
        });
        return next;
      });
      preselectionAppliedRef.current = true;
    }

    if (nav.hint === "select-for-deletion") {
      setHubView("critical");
    }

    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate, setSearchParams]);

  const loadData = useCallback(async () => {
    if (!selectedLocationId) return;

    setListLoading(true);
    setError("");

    try {
      // Fetch stats + the full review set for this location once. Tab
      // filtering and 20-per-page pagination happen client-side from here,
      // so switching tabs/pages is instant and never refetches.
      const [statsData, reviewsData] = await Promise.all([
        fetchLocationStats(selectedLocationId),
        fetchLocationReviews(selectedLocationId, {
          page: 1,
          pageSize: HUB_BATCH_SIZE,
        }),
      ]);
      setStats(statsData);
      setHubReviews(reviewsData.reviews);
      // Authoritative total from the paginator count (never cached).
      setTotalCount(reviewsData.count ?? reviewsData.reviews.length);
    } catch (err) {
      setError(err?.data?.detail || t("reviewHub.loadError"));
      setStats(null);
      setHubReviews([]);
      setTotalCount(0);
    } finally {
      setListLoading(false);
      setFirstLoadDone(true);
    }
  }, [selectedLocationId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /*
   * Auto-refresh from the platform whenever the hub is opened — the user
   * never needs to press the sync button. Reviews already in our DB render
   * immediately; the fresh pull happens in the background and the list
   * reloads when it finishes. Throttled per location (session) so switching
   * tabs back and forth does not hammer the Google/Jameda APIs.
   */
  useEffect(() => {
    if (!selectedLocationId) return undefined;
    const selected = locations.find((l) => l.locationId === selectedLocationId);
    if (!selected) return undefined;

    const AUTO_SYNC_TTL_MS = 10 * 60 * 1000;
    const throttleKey = `repman-hub-autosync:${selectedLocationId}`;
    let last = 0;
    try {
      last = Number(window.sessionStorage.getItem(throttleKey) || 0);
    } catch {
      /* ignore */
    }
    if (Date.now() - last < AUTO_SYNC_TTL_MS) return undefined;
    try {
      window.sessionStorage.setItem(throttleKey, String(Date.now()));
    } catch {
      /* ignore */
    }

    let cancelled = false;
    (async () => {
      setSyncing(true);
      try {
        if (selected.platform === "jameda") {
          await syncJameda();
        } else {
          await syncLocationReviews(selectedLocationId, { incremental: true });
        }
        if (!cancelled) await loadData();
      } catch {
        /* background refresh is best-effort; the DB copy is already shown */
      } finally {
        if (!cancelled) setSyncing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLocationId, locations, loadData]);

  // Full filtered set for the active tab (all pages).
  const visibleReviews = useMemo(
    () => filterByView(hubReviews, hubView, resolvedIds, showResolved),
    [hubReviews, hubView, resolvedIds, showResolved]
  );

  // The 20 reviews shown on the current page.
  const pagedReviews = useMemo(
    () => visibleReviews.slice((page - 1) * ALL_PAGE_SIZE, page * ALL_PAGE_SIZE),
    [visibleReviews, page]
  );

  // Selection acts on what's actually on screen (the current page).
  const selectableReviews = useMemo(
    () => pagedReviews.filter((review) => !review.deletionCase),
    [pagedReviews]
  );

  const viewCounts = useMemo(
    () => countByView(hubReviews, resolvedIds, { ...stats, totalReviews: totalCount }),
    [hubReviews, resolvedIds, stats, totalCount]
  );

  const selectedReviews = useMemo(
    () => Object.values(selectedById).filter((review) => !review.deletionCase),
    [selectedById]
  );

  const isCriticalView = hubView === "critical";

  const selectedLocation = locations.find((l) => l.locationId === selectedLocationId);
  // Pages are computed from the current tab's filtered reviews (20 per page).
  const totalPages = Math.max(1, Math.ceil(visibleReviews.length / ALL_PAGE_SIZE));

  const toggleRemovalSelection = (review) => {
    if (!isCriticalRating(review.rating) || review.deletionCase) return;
    setSelectedById((prev) => {
      const next = { ...prev };
      if (next[review.id]) delete next[review.id];
      else next[review.id] = review;
      return next;
    });
  };

  const clearSelection = () => setSelectedById({});

  const toggleSelectAllVisible = () => {
    if (selectableReviews.length === 0) return;
    const allSelected = selectableReviews.every((r) => selectedById[r.id]);
    if (allSelected) {
      setSelectedById((prev) => {
        const next = { ...prev };
        selectableReviews.forEach((r) => delete next[r.id]);
        return next;
      });
    } else {
      setSelectedById((prev) => {
        const next = { ...prev };
        selectableReviews.forEach((r) => {
          next[r.id] = r;
        });
        return next;
      });
    }
  };

  const allSelectableSelected =
    selectableReviews.length > 0 &&
    selectableReviews.every((r) => selectedById[r.id]);

  const handleSync = async () => {
    if (!selectedLocationId) return;
    const selected = locations.find((l) => l.locationId === selectedLocationId);
    setSyncing(true);
    setSyncMessage("");
    setError("");
    try {
      if (selected?.platform === "jameda") {
        await syncJameda();
      } else {
        await syncLocationReviews(selectedLocationId, { incremental: false });
      }
      setSyncMessage(t("reviewHub.syncSuccess"));
      await loadData();
    } catch (err) {
      setError(err?.data?.detail || t("reviewHub.syncFailed"));
    } finally {
      setSyncing(false);
    }
  };

  const handleSendReply = async (review, comment) => {
    setSendingReviewId(review.id);
    setError("");
    try {
      await postReviewReply(review.id, comment);
      setPendingReplies((c) => ({ ...c, [review.id]: comment }));
      setExpandedReplyId(null);
      await loadData();
      setPendingReplies((c) => {
        const next = { ...c };
        delete next[review.id];
        return next;
      });
    } catch (err) {
      setError(err?.data?.detail || t("reviewHub.replyFailed"));
    } finally {
      setSendingReviewId(null);
    }
  };

  const showPricingSidebar = isCriticalView;

  const pricingPanelProps = useMemo(
    () => ({
      reviewCount: selectedReviews.length,
      billingPlan,
      accountStatus,
      lockPlanSwitch: true,
      subscriberFreeRemaining: freeRemaining,
      freeAllowance,
      freeUsed,
      periodEnd,
      isProfessional: isPaid,
      totalReviews: totalCount,
      currentRating: stats?.averageRating ?? 0,
      selectedReviews,
      onClear: clearSelection,
      onContinue: () => setCaseModalOpen(true),
    }),
    [
      selectedReviews,
      billingPlan,
      accountStatus,
      freeRemaining,
      freeAllowance,
      freeUsed,
      periodEnd,
      isPaid,
      totalCount,
      stats?.averageRating,
    ]
  );

  if (locations.length === 0) {
    return (
      <div className="mx-auto max-w-container-max px-margin-mobile py-stack-lg md:px-margin-desktop">
        <div className="rounded-2xl border border-outline-variant bg-surface-container-low p-10 text-center">
          <MaterialIcon name="link_off" className="mb-4 text-4xl text-outline" />
          <h1 className="font-headline-md text-primary">{t("reviewHub.noProfile")}</h1>
          <p className="mt-2 text-body-md text-on-surface-variant">
            {t("reviewHub.noProfileHint")}
          </p>
          <Link
            to="/dashboard/client/platforms"
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 font-label-caps text-on-primary"
          >
            {t("reviewHub.connectPlatform")}
            <MaterialIcon name="arrow_forward" />
          </Link>
        </div>
      </div>
    );
  }

  const cardLayout = isCriticalView ? "grid" : "list";

  return (
    <div className="bg-background font-body-md text-on-surface">
      <div className="mx-auto max-w-container-max px-margin-mobile py-stack-lg md:px-margin-desktop">
        <header className="mb-stack-lg flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="font-headline-lg text-headline-lg text-primary">{t("reviewHub.title")}</h1>
            <p className="mt-1 max-w-2xl text-body-md text-on-surface-variant">
              {t("reviewHub.subtitle")}
            </p>
            <div className="mt-2">
              <AccountStatusBadge status={accountStatus} />
            </div>
            {isPaid && benefits ? (
              <div className="mt-4 max-w-xl">
                <ProfessionalBenefitsBanner
                  freeRemaining={freeRemaining}
                  freeAllowance={freeAllowance}
                  freeUsed={freeUsed}
                  periodEnd={periodEnd}
                  compact
                />
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded-lg border border-outline-variant px-4 py-2 text-label-sm hover:bg-surface-container-low disabled:opacity-50"
          >
            <MaterialIcon name="sync" className={`text-[18px] ${syncing ? "animate-spin" : ""}`} />
            {syncing ? t("reviewHub.syncing") : t("reviewHub.syncReviews")}
          </button>
        </header>

        {locations.length > 1 ? (
          <div className="mb-stack-md flex flex-wrap gap-2">
            {locations.map((loc) => (
              <button
                key={loc.locationId}
                type="button"
                onClick={() => setSelectedLocationId(loc.locationId)}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-label-sm ${
                  loc.locationId === selectedLocationId
                    ? "border-secondary bg-secondary-container text-on-secondary-container"
                    : "border-outline-variant bg-surface-container-lowest text-on-surface-variant"
                }`}
              >
                {loc.platform === "jameda" ? (
                  <JamedaLogo className="h-4 w-4" />
                ) : (
                  <GoogleBusinessLogo className="h-4 w-4" />
                )}
                {loc.displayName}
              </button>
            ))}
          </div>
        ) : selectedLocation ? (
          <p className="mb-stack-md text-label-sm text-on-surface-variant">
            {t("reviewHub.profile")}: <span className="font-medium text-primary">{selectedLocation.displayName}</span>
          </p>
        ) : null}

        {error ? (
          <div className="mb-4 rounded-xl border border-error-container bg-error-container/40 px-4 py-3 text-on-error-container">
            {error}
          </div>
        ) : null}
        {syncMessage ? (
          <div className="mb-4 rounded-xl border border-secondary/30 bg-secondary-container/30 px-4 py-3 text-on-secondary-container">
            {syncMessage}
          </div>
        ) : null}

        <ReviewsMetricsCards
          stats={stats ? { ...stats, totalReviews: totalCount } : stats}
          loading={!firstLoadDone || (listLoading && !stats)}
        />

        <div className="mb-6 mt-8">
          <ReviewHubViewTabs activeView={hubView} onChange={setHubView} counts={viewCounts} />
        </div>

        <section className="flex flex-col gap-gutter xl:flex-row xl:items-start">
          <div className="min-w-0 flex-1">
            {isCriticalView && visibleReviews.length > 0 ? (
              <div className="mb-4 flex flex-col items-end gap-1">
                {selectableReviews.length > 0 ? (
                  <button
                    type="button"
                    onClick={toggleSelectAllVisible}
                    className="inline-flex items-center gap-2 rounded-lg border border-primary px-3 py-1.5 text-label-sm text-primary hover:bg-primary/5"
                  >
                    <MaterialIcon name="rule" className="text-[16px]" />
                    {allSelectableSelected
                      ? t("reviewHub.deselectAll")
                      : t("reviewHub.selectAllVisible")}
                  </button>
                ) : (
                  <p className="text-label-sm text-on-surface-variant">
                    {t("reviewHub.allVisibleInDeletionCase")}
                  </p>
                )}
              </div>
            ) : null}

            {listLoading || !firstLoadDone ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="tonal-card h-40 animate-pulse rounded-2xl" />
                ))}
              </div>
            ) : visibleReviews.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-outline-variant bg-surface-container-low py-20 text-center">
                <MaterialIcon name="check_circle" className="mb-4 text-5xl text-secondary" filled />
                <h3 className="font-headline-md text-primary">
                  {hubView === "critical" ? t("reviewHub.noCritical") : t("reviewHub.allCaughtUp")}
                </h3>
                <p className="mt-1 text-on-surface-variant">
                  {hubView === "needs-reply"
                    ? t("reviewHub.everyReviewReplied")
                    : hubView === "critical"
                      ? t("reviewHub.nothingHurtingRating")
                      : t("reviewHub.noReviewsToShow")}
                </p>
              </div>
            ) : isCriticalView ? (
              <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2">
                {pagedReviews.map((review) => (
                  <li key={review.id} className="min-w-0">
                    <ReviewHubCard
                      review={review}
                      layout={cardLayout}
                      removalSelectionEnabled
                      selectedForRemoval={Boolean(selectedById[review.id])}
                      onToggleRemovalSelection={() => toggleRemovalSelection(review)}
                      replyComposerOpen={expandedReplyId === review.id}
                      onToggleReply={(id) =>
                        setExpandedReplyId((current) => (current === id ? null : id))
                      }
                      onSendReply={handleSendReply}
                      sending={sendingReviewId === review.id}
                      pendingReplyText={pendingReplies[review.id]}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="space-y-5">
                {pagedReviews.map((review) => (
                  <ReviewHubCard
                    key={review.id}
                    review={review}
                    layout={cardLayout}
                    removalSelectionEnabled={false}
                    replyComposerOpen={expandedReplyId === review.id}
                    onToggleReply={(id) =>
                      setExpandedReplyId((current) => (current === id ? null : id))
                    }
                    onSendReply={handleSendReply}
                    sending={sendingReviewId === review.id}
                    pendingReplyText={pendingReplies[review.id]}
                  />
                ))}
              </div>
            )}

            {totalPages > 1 ? (
              <div className="mt-6 flex items-center justify-center gap-3">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-outline-variant px-4 py-2 text-label-sm disabled:opacity-40"
                >
                  {t("reviewHub.previous")}
                </button>
                <span className="text-label-sm text-on-surface-variant">
                  {t("reviewHub.pageOf", { page, total: totalPages })}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded-lg border border-outline-variant px-4 py-2 text-label-sm disabled:opacity-40"
                >
                  {t("reviewHub.next")}
                </button>
              </div>
            ) : null}
          </div>

          {showPricingSidebar ? (
            <div className="w-full shrink-0 xl:w-[360px]">
              <div className="sticky top-6">
                <ReviewSelectionPricingPanel {...pricingPanelProps} />
              </div>
            </div>
          ) : null}
        </section>
      </div>

      <CreateDeletionCaseModal
        open={caseModalOpen}
        onClose={() => setCaseModalOpen(false)}
        selectedReviews={selectedReviews}
        isProfessional={isPaid}
        subscriberFreeRemaining={freeRemaining}
        onSuccess={async (created) => {
          await refreshBenefits();
          clearSelection();
          setCaseModalOpen(false);
          navigate("/dashboard/client/deletion-cases", {
            state: {
              expandCaseId: created.id,
              caseSubmitted: t("reviewHub.caseSubmitted", {
                caseId: formatCaseId(created),
              }),
            },
          });
        }}
      />
    </div>
  );
};

export default ClientReviewHubPage;
